import React, { useMemo, useState, useRef, useCallback } from 'react';
import Tesseract from 'tesseract.js';
import { Camera, X, Loader, FileText, Crop, RotateCcw, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Map app language codes to Tesseract language codes
const getTesseractLang = (appLang) => {
  const langMap = {
    en: 'eng', es: 'spa', de: 'deu', fr: 'fra', it: 'ita', pt: 'por',
    ar: 'ara', ru: 'rus', tr: 'tur', pl: 'pol', nl: 'nld', sv: 'swe',
    da: 'dan', fi: 'fin', no: 'nor', el: 'ell', cs: 'ces', hu: 'hun',
    ro: 'ron', uk: 'ukr', bg: 'bul', sr: 'srp', hi: 'hin', bn: 'ben',
    id: 'ind', ms: 'msa', th: 'tha', vi: 'vie', ur: 'urd'
  };
  return langMap[appLang] || 'eng';
};

// Image optimization: resize + enhance for faster OCR
const optimizeImageForOCR = (imageSrc, maxWidth = 1200) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Calculate new dimensions (resize if larger than maxWidth)
      let width = img.width;
      let height = img.height;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and enhance image
      ctx.drawImage(img, 0, 0, width, height);
      
      // Apply contrast enhancement for better OCR
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      const contrast = 1.3; // Increase contrast
      const factor = (259 * (contrast * 100 + 255)) / (255 * (259 - contrast * 100));
      
      for (let i = 0; i < data.length; i += 4) {
        // Convert to grayscale
        const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        // Apply contrast
        const enhanced = Math.min(255, Math.max(0, factor * (gray - 128) + 128));
        data[i] = data[i + 1] = data[i + 2] = enhanced;
      }
      
      ctx.putImageData(imageData, 0, 0);
      
      canvas.toBlob(resolve, 'image/jpeg', 0.9);
    };
    img.src = imageSrc;
  });
};

// Crop image using canvas
const cropImage = (imageSrc, cropArea, displaySize) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Calculate scale between display size and actual image size
      const scaleX = img.width / displaySize.width;
      const scaleY = img.height / displaySize.height;
      
      // Calculate actual crop dimensions
      const actualX = cropArea.x * scaleX;
      const actualY = cropArea.y * scaleY;
      const actualWidth = cropArea.width * scaleX;
      const actualHeight = cropArea.height * scaleY;
      
      canvas.width = actualWidth;
      canvas.height = actualHeight;
      
      ctx.drawImage(
        img,
        actualX, actualY, actualWidth, actualHeight,
        0, 0, actualWidth, actualHeight
      );
      
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    img.src = imageSrc;
  });
};

export default function IngredientScanner({ onClose, onAnalyze }) {
  const { t, i18n } = useTranslation();
  const [text, setText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrError, setOcrError] = useState('');
  
  // Image cropping state
  const [imageSrc, setImageSrc] = useState(null);
  const [cropMode, setCropMode] = useState(false);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragAction, setDragAction] = useState(null); // 'draw' | 'move' | 'resize-tl' | 'resize-tr' | 'resize-bl' | 'resize-br'
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef(null);
  const imageRef = useRef(null);

  const HANDLE_SIZE = 20; // Size of resize handles

  const canAnalyze = useMemo(() => text.trim().length > 0 && !isProcessing, [text, isProcessing]);

  // Get image display size for accurate crop calculations
  const getDisplaySize = useCallback(() => {
    if (imageRef.current) {
      return {
        width: imageRef.current.clientWidth,
        height: imageRef.current.clientHeight
      };
    }
    return { width: 0, height: 0 };
  }, []);

  // Determine what action to take based on click position
  const getClickAction = useCallback((x, y) => {
    if (cropArea.width < 20 || cropArea.height < 20) return 'draw';
    
    const handleHitArea = HANDLE_SIZE;
    
    // Check if clicking on resize handles (corners)
    const nearLeft = Math.abs(x - cropArea.x) < handleHitArea;
    const nearRight = Math.abs(x - (cropArea.x + cropArea.width)) < handleHitArea;
    const nearTop = Math.abs(y - cropArea.y) < handleHitArea;
    const nearBottom = Math.abs(y - (cropArea.y + cropArea.height)) < handleHitArea;
    
    if (nearLeft && nearTop) return 'resize-tl';
    if (nearRight && nearTop) return 'resize-tr';
    if (nearLeft && nearBottom) return 'resize-bl';
    if (nearRight && nearBottom) return 'resize-br';
    
    // Check if clicking inside crop area (move)
    if (x >= cropArea.x && x <= cropArea.x + cropArea.width &&
        y >= cropArea.y && y <= cropArea.y + cropArea.height) {
      return 'move';
    }
    
    // Otherwise, draw new selection
    return 'draw';
  }, [cropArea, HANDLE_SIZE]);

  // Handle mouse/touch down for crop selection
  const handleCropStart = useCallback((e) => {
    if (!cropMode || !imageContainerRef.current) return;
    
    e.preventDefault();
    const rect = imageContainerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    const action = getClickAction(x, y);
    setDragAction(action);
    setDragStart({ x, y });
    setIsDragging(true);
    
    if (action === 'draw') {
      setCropArea({ x, y, width: 0, height: 0 });
    } else if (action === 'move') {
      setDragOffset({ x: x - cropArea.x, y: y - cropArea.y });
    }
  }, [cropMode, getClickAction, cropArea]);

  // Handle mouse/touch move for crop selection
  const handleCropMove = useCallback((e) => {
    if (!isDragging || !imageContainerRef.current || !dragAction) return;
    
    e.preventDefault();
    const rect = imageContainerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const currentX = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const currentY = Math.max(0, Math.min(clientY - rect.top, rect.height));
    
    if (dragAction === 'draw') {
      const x = Math.min(dragStart.x, currentX);
      const y = Math.min(dragStart.y, currentY);
      const width = Math.abs(currentX - dragStart.x);
      const height = Math.abs(currentY - dragStart.y);
      setCropArea({ x, y, width, height });
    } else if (dragAction === 'move') {
      const newX = Math.max(0, Math.min(currentX - dragOffset.x, rect.width - cropArea.width));
      const newY = Math.max(0, Math.min(currentY - dragOffset.y, rect.height - cropArea.height));
      setCropArea(prev => ({ ...prev, x: newX, y: newY }));
    } else if (dragAction.startsWith('resize-')) {
      let newX = cropArea.x;
      let newY = cropArea.y;
      let newWidth = cropArea.width;
      let newHeight = cropArea.height;
      
      // Handle corner resizing
      if (dragAction === 'resize-tl') {
        newWidth = cropArea.x + cropArea.width - currentX;
        newHeight = cropArea.y + cropArea.height - currentY;
        if (newWidth > 20) newX = currentX;
        if (newHeight > 20) newY = currentY;
      } else if (dragAction === 'resize-tr') {
        newWidth = currentX - cropArea.x;
        newHeight = cropArea.y + cropArea.height - currentY;
        if (newHeight > 20) newY = currentY;
      } else if (dragAction === 'resize-bl') {
        newWidth = cropArea.x + cropArea.width - currentX;
        newHeight = currentY - cropArea.y;
        if (newWidth > 20) newX = currentX;
      } else if (dragAction === 'resize-br') {
        newWidth = currentX - cropArea.x;
        newHeight = currentY - cropArea.y;
      }
      
      // Ensure minimum size
      newWidth = Math.max(20, newWidth);
      newHeight = Math.max(20, newHeight);
      
      setCropArea({ x: newX, y: newY, width: newWidth, height: newHeight });
    }
  }, [isDragging, dragStart, dragAction, dragOffset, cropArea]);

  // Handle mouse/touch up for crop selection
  const handleCropEnd = useCallback(() => {
    setIsDragging(false);
    setDragAction(null);
  }, []);

  // Run OCR on the image
  const runOCR = useCallback(async (imageToProcess) => {
    setIsProcessing(true);
    setOcrProgress(0);
    setOcrError('');

    try {
      // Optimize image before OCR
      const optimizedBlob = await optimizeImageForOCR(imageToProcess);
      
      // Use multi-language OCR: current app language + English fallback
      const currentLang = getTesseractLang(i18n.language);
      const ocrLangs = currentLang === 'eng' ? 'eng' : `${currentLang}+eng`;
      
      const { data } = await Tesseract.recognize(optimizedBlob, ocrLangs, {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            setOcrProgress(Math.round(m.progress * 100));
          }
        }
      });
      
      const scannedText = String(data?.text || '').trim();
      if (!scannedText) {
        setOcrError(t('scanner.noTextFound', { defaultValue: 'No readable text found. Try another photo.' }));
      } else {
        setText(prev => (prev ? `${prev}\n${scannedText}` : scannedText));
        // Clear image after successful OCR
        setImageSrc(null);
        setCropMode(false);
      }
    } catch (error) {
      console.error('OCR failed:', error);
      setOcrError(t('scanner.ocrFailed', { defaultValue: 'Unable to read text from image. You can paste ingredients manually.' }));
    } finally {
      setIsProcessing(false);
      setOcrProgress(0);
    }
  }, [i18n.language, t]);

  // Apply crop and run OCR
  const applyCropAndOCR = useCallback(async () => {
    if (!imageSrc) return;
    
    if (cropMode && cropArea.width > 20 && cropArea.height > 20) {
      // Crop the image first
      const displaySize = getDisplaySize();
      const croppedImage = await cropImage(imageSrc, cropArea, displaySize);
      await runOCR(croppedImage);
    } else {
      // Use full image
      await runOCR(imageSrc);
    }
    
    setCropArea({ x: 0, y: 0, width: 0, height: 0 });
  }, [imageSrc, cropMode, cropArea, getDisplaySize, runOCR]);

  // Handle file selection
  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Read file as data URL for preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target.result);
      setCropMode(true);
      setCropArea({ x: 0, y: 0, width: 0, height: 0 });
      setOcrError('');
    };
    reader.readAsDataURL(file);
    
    event.target.value = '';
  };

  // Cancel crop and clear image
  const cancelCrop = useCallback(() => {
    setImageSrc(null);
    setCropMode(false);
    setCropArea({ x: 0, y: 0, width: 0, height: 0 });
  }, []);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(17, 24, 39, 0.75)',
      backdropFilter: 'blur(4px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      overflow: 'auto'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '640px',
        maxHeight: '90vh',
        background: 'var(--bg-card)',
        borderRadius: '20px',
        padding: '20px',
        boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: '1px solid var(--border-color)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
              borderRadius: '10px',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FileText size={20} color="white" />
            </div>
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '18px', fontWeight: '600' }}>
              {t('scanner.ingredientsScanTitle', { defaultValue: 'Scan Ingredients' })}
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'var(--bg-secondary)',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              borderRadius: '8px',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Image preview and crop UI */}
        {imageSrc && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              marginBottom: '12px',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              padding: '12px 14px',
              borderRadius: '12px',
              fontSize: '13px',
              color: '#92400e',
              border: '1px solid #fcd34d'
            }}>
              <div style={{
                background: '#f59e0b',
                borderRadius: '8px',
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Crop size={14} color="white" />
              </div>
              <span style={{ flex: 1 }}>
                {t('scanner.cropHint', { defaultValue: 'Drag to select ingredients area, or scan full image' })}
              </span>
            </div>
            
            <div 
              ref={imageContainerRef}
              style={{ 
                position: 'relative',
                borderRadius: '10px',
                overflow: 'hidden',
                cursor: cropMode ? 'crosshair' : 'default',
                touchAction: 'none'
              }}
              onMouseDown={handleCropStart}
              onMouseMove={handleCropMove}
              onMouseUp={handleCropEnd}
              onMouseLeave={handleCropEnd}
              onTouchStart={handleCropStart}
              onTouchMove={handleCropMove}
              onTouchEnd={handleCropEnd}
            >
              <img 
                ref={imageRef}
                src={imageSrc} 
                alt="Captured" 
                style={{ 
                  width: '100%', 
                  display: 'block',
                  userSelect: 'none',
                  pointerEvents: 'none'
                }} 
              />
              
              {/* Crop overlay */}
              {cropArea.width > 0 && cropArea.height > 0 && (
                <>
                  {/* Darkened overlay outside crop area */}
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.5)',
                    clipPath: `polygon(
                      0 0, 100% 0, 100% 100%, 0 100%, 0 0,
                      ${cropArea.x}px ${cropArea.y}px,
                      ${cropArea.x}px ${cropArea.y + cropArea.height}px,
                      ${cropArea.x + cropArea.width}px ${cropArea.y + cropArea.height}px,
                      ${cropArea.x + cropArea.width}px ${cropArea.y}px,
                      ${cropArea.x}px ${cropArea.y}px
                    )`,
                    pointerEvents: 'none'
                  }} />
                  
                  {/* Crop selection border */}
                  <div style={{
                    position: 'absolute',
                    left: cropArea.x,
                    top: cropArea.y,
                    width: cropArea.width,
                    height: cropArea.height,
                    border: '2px solid #059669',
                    borderRadius: '4px',
                    pointerEvents: 'none'
                  }} />
                  
                  {/* Corner resize handles */}
                  {/* Top-left */}
                  <div style={{
                    position: 'absolute',
                    left: cropArea.x - 6,
                    top: cropArea.y - 6,
                    width: 14,
                    height: 14,
                    background: '#059669',
                    border: '2px solid white',
                    borderRadius: '3px',
                    cursor: 'nwse-resize',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }} />
                  
                  {/* Top-right */}
                  <div style={{
                    position: 'absolute',
                    left: cropArea.x + cropArea.width - 8,
                    top: cropArea.y - 6,
                    width: 14,
                    height: 14,
                    background: '#059669',
                    border: '2px solid white',
                    borderRadius: '3px',
                    cursor: 'nesw-resize',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }} />
                  
                  {/* Bottom-left */}
                  <div style={{
                    position: 'absolute',
                    left: cropArea.x - 6,
                    top: cropArea.y + cropArea.height - 8,
                    width: 14,
                    height: 14,
                    background: '#059669',
                    border: '2px solid white',
                    borderRadius: '3px',
                    cursor: 'nesw-resize',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }} />
                  
                  {/* Bottom-right */}
                  <div style={{
                    position: 'absolute',
                    left: cropArea.x + cropArea.width - 8,
                    top: cropArea.y + cropArea.height - 8,
                    width: 14,
                    height: 14,
                    background: '#059669',
                    border: '2px solid white',
                    borderRadius: '3px',
                    cursor: 'nwse-resize',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }} />
                  
                  {/* Move indicator in center */}
                  {cropArea.width > 60 && cropArea.height > 60 && (
                    <div style={{
                      position: 'absolute',
                      left: cropArea.x + cropArea.width / 2 - 16,
                      top: cropArea.y + cropArea.height / 2 - 16,
                      width: 32,
                      height: 32,
                      background: 'rgba(5, 150, 105, 0.8)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'move',
                      pointerEvents: 'none',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                        <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20"/>
                      </svg>
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Crop action buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={cancelCrop}
                disabled={isProcessing}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  border: '2px solid var(--border-color)',
                  background: 'var(--bg-card)',
                  color: 'var(--text-secondary)',
                  borderRadius: '10px',
                  padding: '10px 16px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isProcessing ? 0.5 : 1
                }}
              >
                <RotateCcw size={16} />
                {t('scanner.retake', { defaultValue: 'Retake' })}
              </button>
              
              <button
                onClick={applyCropAndOCR}
                disabled={isProcessing}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  border: 'none',
                  background: isProcessing
                    ? '#9ca3af'
                    : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                  color: 'white',
                  borderRadius: '10px',
                  padding: '10px 16px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  flex: 1,
                  boxShadow: isProcessing ? 'none' : '0 4px 12px rgba(139, 92, 246, 0.3)'
                }}
              >
                {isProcessing ? (
                  <>
                    <Loader size={14} className="spin" />
                    {t('scanner.processingProgress', { progress: ocrProgress, defaultValue: `Reading... ${ocrProgress}%` })}
                  </>
                ) : (
                  <>
                    <Check size={14} />
                    {cropArea.width > 20 && cropArea.height > 20
                      ? t('scanner.scanSelected', { defaultValue: 'Scan Selected Area' })
                      : t('scanner.scanFull', { defaultValue: 'Scan Full Image' })}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Upload button - only show when no image is loaded */}
        {!imageSrc && (
          <div style={{
            background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
            borderRadius: '14px',
            padding: '20px',
            marginBottom: '16px',
            border: '1px solid #ddd6fe'
          }}>
            <p style={{
              margin: '0 0 14px 0',
              color: '#5b21b6',
              fontSize: '14px',
              fontWeight: '500',
              textAlign: 'center'
            }}>
              {t('scanner.ingredientsScanHint', { defaultValue: 'Take a clear photo of the ingredients list, or paste text manually.' })}
            </p>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
              color: 'white',
              padding: '14px 20px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '15px',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.35)',
              transition: 'transform 0.2s'
            }}>
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '8px',
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Camera size={18} />
              </div>
              {t('scanner.uploadImage', { defaultValue: 'Capture Ingredients Photo' })}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelected}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        )}

        {ocrError && (
          <p style={{ margin: '0 0 12px 0', color: '#b45309', fontSize: '13px' }}>
            ⚠️ {ocrError}
          </p>
        )}

        {/* Text area label */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginBottom: '8px',
          color: 'var(--text-secondary)',
          fontSize: '13px',
          fontWeight: '500'
        }}>
          <FileText size={14} />
          {t('scanner.ingredientsLabel', { defaultValue: 'Ingredients Text' })}
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('scanner.ingredientsTextareaPlaceholder', { defaultValue: 'Paste or edit ingredients text here...' })}
          style={{
            width: '100%',
            minHeight: imageSrc ? '100px' : '160px',
            borderRadius: '12px',
            border: '2px solid var(--border-color)',
            padding: '14px',
            fontSize: '14px',
            color: 'var(--text-primary)',
            background: 'var(--bg-secondary)',
            resize: 'vertical',
            boxSizing: 'border-box',
            outline: 'none',
            transition: 'border-color 0.2s'
          }}
          onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
          onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
        />

        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
          marginTop: '16px'
        }}>
          <button
            onClick={onClose}
            style={{
              border: '2px solid var(--border-color)',
              background: 'var(--bg-card)',
              color: 'var(--text-secondary)',
              borderRadius: '12px',
              padding: '12px 18px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            {t('about.close', { defaultValue: 'Close' })}
          </button>
          <button
            onClick={() => onAnalyze(text)}
            disabled={!canAnalyze}
            style={{
              border: 'none',
              background: canAnalyze
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : '#9ca3af',
              color: 'white',
              borderRadius: '12px',
              padding: '12px 20px',
              cursor: canAnalyze ? 'pointer' : 'not-allowed',
              fontWeight: '600',
              fontSize: '14px',
              boxShadow: canAnalyze ? '0 4px 12px rgba(16, 185, 129, 0.3)' : 'none',
              transition: 'all 0.2s'
            }}
          >
            {t('scanner.analyzeIngredients', { defaultValue: 'Analyze Ingredients' })}
          </button>
        </div>
      </div>
    </div>
  );
}
